import { CSSProperties } from 'react'
import { PlanType, SUBSCRIPTION_BENEFITS } from 'modules/Const'
import ModalSymphony from 'elements/ModalSymphony'
import ButtonSymphony from 'elements/ButtonSymphony'
import useScreen from 'Hooks/useScreen'
import { Body2, Subtitle2 } from 'components/shareable/Typography'
import Colors from 'modules/Colors'
import { styx } from 'modules/Utils'
import useBrand from 'Hooks/useBrand'
import useOrganizationV2 from 'Hooks/useOrganizationV2'
import { useCampaign } from 'Hooks/useCampaign'
import useWebsites from 'Hooks/useWebsites'
import useFans from 'Hooks/useFans'

interface CancellationConfirmationModalProps {
    /** Controls modal visibility */
    isOpen: boolean
    /** Callback when modal is closed */
    onClose: () => void
    /** Callback when cancellation is confirmed */
    onConfirm: () => void
    /** Current plan type */
    currentPlan: PlanType
    /** Whether the user is on a grandfathered plan */
    isGrandfathered: boolean
    /** Whether cancellation is in progress */
    isLoading?: boolean
}

const CancellationConfirmationModal = ({
    isOpen,
    onClose,
    onConfirm,
    currentPlan,
    isGrandfathered,
    isLoading = false
}: CancellationConfirmationModalProps) => {
    const { mobileView } = useScreen()
    const { currentBrand, getFanbaseSize } = useBrand()
    const { fansCount } = useFans()
    const { organization } = useOrganizationV2()
    const { campaigns } = useCampaign()
    const { legacyWebsites, websiteBuilderSites } = useWebsites()

    // Get current subscription status - check organization first (for team plans), then brand
    const getCurrentSubscriptionStatus = (): string | null => {
        // For team plans, check organization status
        if (currentPlan === PlanType.TEAM || currentPlan === PlanType.TEAM_V2) {
            return organization?.stripe_subscription_status || null
        }
        // For individual plans, check brand status
        return currentBrand?.subscription_status || currentBrand?.subscription?.status || null
    }

    const subscriptionStatus = getCurrentSubscriptionStatus()

    // Determine if cancellation will be immediate based on subscription status
    // These statuses require immediate cancellation per Stripe API constraints
    const problematicStatuses = ['paused', 'past_due', 'unpaid', 'incomplete', 'incomplete_expired'];
    const willCancelImmediately = subscriptionStatus && problematicStatuses.includes(subscriptionStatus);

    // Get appropriate messaging based on cancellation type
    const getCancellationMessage = () => {
        if (willCancelImmediately) {
            return {
                subtitle: `Your subscription will be cancelled immediately due to its ${subscriptionStatus} status.`,
                confirmButtonText: 'Yes, Cancel Now',
                warningText: subscriptionStatus === 'paused' 
                    ? 'Since your subscription is paused, it will be cancelled immediately rather than at the end of your billing cycle.'
                    : `Since your subscription is ${subscriptionStatus}, it will be cancelled immediately.`
            };
        }
        return {
            subtitle: 'Your subscription will be cancelled at the end of your billing cycle.',
            confirmButtonText: 'Proceed to Cancel',
            warningText: null
        };
    };

    const cancellationInfo = getCancellationMessage();

    // Calculate user content statistics
    const getUserContentStats = () => {
        const fanbaseSize = getFanbaseSize();
        
        // Count presaves (legacy websites that are presaves)
        const presavesCount = legacyWebsites?.filter((website: any) => 
            website.content_metadata?.presave || website.type === 'presave'
        ).length || 0;
        
        // Count total websites (legacy + website builder)
        const totalWebsites = (legacyWebsites?.length || 0) + (websiteBuilderSites?.length || 0);
        
        // Count campaigns
        const campaignsCount = campaigns?.length || 0;
        
        return {
            fanbaseSize,
            presavesCount,
            websitesCount: totalWebsites,
            campaignsCount
        };
    };

    const contentStats = getUserContentStats();

    /**
     * Get benefits that will be lost based on plan type from centralized configuration
     */
    const getBenefitsToLose = (plan: PlanType): string[] => {
        const planConfig = SUBSCRIPTION_BENEFITS[plan]
        return planConfig?.benefitsToLose || ['Access to paid features']
    }

    const benefits = getBenefitsToLose(currentPlan)
    const planDisplayName = currentPlan === PlanType.TEAM_V2 ? 'Team' : currentPlan

    /**
     * Renders a stat item with automatic pluralization
     */
    const renderStatItem = (value: number, name: string, pluralize: boolean) => {
        if (value <= 0) {
            return null
        }

        const displayName = value === 1 ? name : pluralize ? `${name}s` : name
        
        return (
            <div className="cancellationModalStat" style={sty.statItem}>
                <div style={sty.statNumber}>{value.toLocaleString()}</div>
                <Body2 style={sty.statLabel}>{displayName}</Body2>
            </div>
        )
    }

    /**
     * Gets the top 3 "lose access" items, prioritizing user data and filling with defaults
     */
    const getLoseAccessItems = (): Array<{ value: number; name: string; pluralize: boolean }> => {
        // Collect all user data items that have values > 0
        const userDataItems = [
            { value: fansCount, name: 'Tracked Fan', pluralize: true },
            { value: contentStats.presavesCount, name: 'Presave', pluralize: true },
            { value: contentStats.websitesCount, name: 'Website', pluralize: true },
            { value: contentStats.campaignsCount, name: 'Campaign', pluralize: true }
        ].filter(item => item.value > 0)

        // Sort by highest value first
        userDataItems.sort((a, b) => b.value - a.value)

        // Default items to fill in if we don't have 3 user data items
        const defaultItems = [
            { value: 10, name: 'Hours saved a week', pluralize: false },
            { value: 12, name: 'Campaign playbooks', pluralize: false }
        ]

        // Combine user data with defaults to ensure we always have 3 items
        const allItems = [...userDataItems, ...defaultItems]
        
        // Return top 3 items
        return allItems.slice(0, 3)
    }

    return (
        <ModalSymphony
            className="cancellationConfirmationModal"
            isOpen={isOpen}
            onClose={onClose}
            size="medium"
            fullScreen={mobileView}
            title="Are you sure you want to cancel your subscription?"
            subtitle={cancellationInfo.subtitle}
            buttonLeftText="Keep Subscription"
            buttonRightText={cancellationInfo.confirmButtonText}
            buttonLeftVariant="primary"
            buttonRightVariant="outlined"
            buttonLeftColor="purple"
            buttonRightColor="red"
            buttonRightIsLoading={isLoading}
            buttonRightDisabled={isLoading}
            buttonLeftDisabled={isLoading}
            onClickButtonLeft={onClose}
            onClickButtonRight={onConfirm}
        >
            <div className="cancellationModalContent" style={sty.container}>
                {/* Show warning text for immediate cancellations */}
                {cancellationInfo.warningText && (
                    <div className="cancellationWarning" style={sty.warningBox}>
                        <Body2 style={sty.warningText}>
                            {cancellationInfo.warningText}
                        </Body2>
                    </div>
                )}

                {/* User Content Stats */}
                <div className="cancellationModalStats" style={sty.statsContainer}>
                    <Subtitle2 style={sty.statsTitle}>You'll lose access to:</Subtitle2>
                    <div className="cancellationModalStatsGrid" style={sty.statsGrid}>
                        {getLoseAccessItems().map((item, index) => (
                            <div key={`${item.name}-${item.value}`}>
                                {renderStatItem(item.value, item.name, item.pluralize)}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Benefits Lost */}
                <div className="cancellationModalBenefits" style={sty.benefitsList}>
                    <Subtitle2 style={sty.benefitsTitle}>Premium features you'll lose:</Subtitle2>
                    {benefits.map((benefit, index) => (
                        <div key={index} className="cancellationModalBenefit" style={sty.benefitItem}>
                            <div style={sty.bulletPoint}>•</div>
                            <Body2 style={sty.benefitText}>{benefit}</Body2>
                        </div>
                    ))}
                </div>
            </div>
        </ModalSymphony>
    )
}

const sty: Record<string, CSSProperties> = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        padding: 24,
        paddingTop: 0
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        textAlign: 'center'
    },
    title: {
        fontWeight: 600,
        color: Colors.black
    },
    subtitle: {
        color: Colors.grey
    },
    benefitsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: '16px 0'
    },
    benefitItem: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 8
    },
    bulletPoint: {
        color: Colors.purple,
        fontWeight: 600,
        fontSize: 16,
        lineHeight: '24px',
        minWidth: 8
    },
    benefitText: {
        color: Colors.black,
        flex: 1
    },
    actions: {
        display: 'flex',
        flexDirection: 'row',
        gap: 12,
        justifyContent: 'flex-end'
    },
    cancelButton: {
        minWidth: 140
    },
    warningBox: {
        backgroundColor: '#FEF3C7', // Light yellow background
        border: '1px solid #F59E0B', // Yellow border
        borderRadius: 8,
        padding: 16,
        marginBottom: 16
    },
    warningText: {
        color: '#92400E', // Dark yellow/brown text
        fontSize: 14,
        lineHeight: '20px',
        margin: 0
    },
    statsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: '20px 0',
        borderBottom: '1px solid #E5E7EB'
    },
    statsTitle: {
        color: Colors.black,
        fontWeight: 600,
        marginBottom: 8
    },
    statsGrid: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 20,
        justifyContent: 'space-around'
    },
    statItem: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: 80
    },
    statNumber: {
        fontSize: 24,
        fontWeight: 700,
        color: Colors.purple,
        lineHeight: '28px'
    },
    statLabel: {
        fontSize: 16,
        color: Colors.greyDark,
        textAlign: 'center',
        marginTop: 4
    },
    benefitsTitle: {
        color: Colors.black,
        fontWeight: 600,
        marginBottom: 12
    }
}

export default CancellationConfirmationModal 